import React, { useEffect, useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { useAuthStore } from '../lib/auth';
import { boardsAPI, usersAPI, dailyChallengeAPI, studyPathAPI } from '../lib/api';
import { toast } from 'sonner';
import OnboardingWalkthrough from './OnboardingWalkthrough';
import { 
  Plus, Zap, Users, Trophy, LayoutGrid, LogOut, Target, TrendingUp, 
  User, Settings, ChevronDown, Calendar, Flame, CheckCircle, 
  GraduationCap, Clock, Play, ArrowUp, Gamepad2, BarChart3,
  Sparkles, AlertTriangle, ChevronRight, Award, Brain, Crown, Medal, Folder, Swords
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

export default function Dashboard() {
  useSEO('Dashboard | JepPrep', 'Your JepPrep dashboard. Track your progress, view stats, and jump into trivia practice modes.');
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [recentBoards, setRecentBoards] = useState([]);
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [dailyLeaderboard, setDailyLeaderboard] = useState([]);
  const [studyPath, setStudyPath] = useState(null);
  const [achievements, setAchievements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (user && user.has_seen_onboarding === false) {
      setShowOnboarding(true);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, boardsRes, dailyRes, leaderboardRes, studyRes, achievementsRes] = await Promise.all([
        usersAPI.getMyStats(),
        boardsAPI.list({ limit: 5 }),
        dailyChallengeAPI.get().catch(() => null),
        dailyChallengeAPI.getLeaderboard().catch(() => null),
        studyPathAPI.getRecommendations().catch(() => null),
        usersAPI.getAchievements().catch(() => null)
      ]);
      setStats(statsRes.data);
      setRecentBoards(boardsRes.data.boards || []);
      if (dailyRes) {
        setDailyChallenge(dailyRes.data);
      }
      if (leaderboardRes) {
        setDailyLeaderboard(leaderboardRes.data.leaderboard?.slice(0, 3) || []);
      }
      if (studyRes) {
        setStudyPath(studyRes.data);
      }
      if (achievementsRes) {
        setAchievements(achievementsRes.data);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  // Get top and bottom categories from Sprint stats
  const getTopCategories = () => {
    if (!stats?.sprint_category_performance) return [];
    return Object.entries(stats.sprint_category_performance)
      .map(([cat, data]) => ({
        name: cat,
        accuracy: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0,
        total: data.total
      }))
      .filter(cat => cat.total >= 5)
      .sort((a, b) => b.accuracy - a.accuracy)
      .slice(0, 4);
  };

  const getWeakCategories = () => {
    if (!stats?.sprint_category_performance) return [];
    return Object.entries(stats.sprint_category_performance)
      .map(([cat, data]) => ({
        name: cat,
        accuracy: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0,
        total: data.total
      }))
      .filter(cat => cat.total >= 5 && cat.accuracy < 60)
      .sort((a, b) => a.accuracy - b.accuracy)
      .slice(0, 3);
  };

  const topCategories = getTopCategories();
  const weakCategories = getWeakCategories();

  // Daily Challenge state helpers
  const isDailyCompleted = dailyChallenge?.user_completed;
  const isDailyInProgress = dailyChallenge?.user_submission?.status === 'in_progress';
  const userSubmission = dailyChallenge?.user_submission;

  // Format time from ms
  const formatTime = (ms) => {
    if (!ms) return '0m 0s';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Onboarding Walkthrough */}
      {showOnboarding && (
        <OnboardingWalkthrough onComplete={() => {
          setShowOnboarding(false);
          // Update local user state so onboarding never shows again
          const updatedUser = { ...user, has_seen_onboarding: true };
          localStorage.setItem('jepprep_user', JSON.stringify(updatedUser));
          useAuthStore.setState({ user: updatedUser });
        }} />
      )}
      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link to="/dashboard">
              <img src={LOGO_URL} alt="JepPrep" className="h-12" />
            </Link>
            <div className="flex items-center gap-1 sm:gap-4">
              <Link to="/boards">
                <Button variant="ghost" className="text-gray-300 hover:text-white text-xs sm:text-sm px-2 sm:px-4" data-testid="nav-boards">
                  My Boards
                </Button>
              </Link>
              <Link to="/community">
                <Button variant="ghost" className="text-gray-300 hover:text-white text-xs sm:text-sm px-2 sm:px-4" data-testid="nav-community">
                  <span className="sm:hidden">Community</span>
                  <span className="hidden sm:inline">Community Boards</span>
                </Button>
              </Link>
              
              {/* Profile Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-gray-300 hover:text-white flex items-center gap-1 sm:gap-2 px-2 sm:px-4" data-testid="nav-profile-dropdown">
                    <div className="w-8 h-8 rounded-full bg-[hsl(var(--brand-blue-800))] overflow-hidden border border-[hsl(var(--brand-gold-500))]">
                      {user?.avatar_url ? (
                        <img src={user.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-full h-full p-1" />
                      )}
                    </div>
                    <span className="hidden sm:inline">{user?.display_name || 'Player'}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] min-w-[160px]">
                  <DropdownMenuItem asChild className="text-gray-300 hover:text-white focus:text-white cursor-pointer">
                    <Link to="/profile" className="flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Profile Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="text-gray-300 hover:text-white focus:text-white cursor-pointer">
                    <Link to="/achievements" className="flex items-center gap-2">
                      <Trophy className="w-4 h-4" />
                      Achievements
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[hsl(var(--border))]" />
                  <DropdownMenuItem 
                    onClick={handleLogout} 
                    className="text-gray-300 hover:text-white focus:text-white cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        
        {/* ============ DAILY CHALLENGE & LEADERBOARD TILES ============ */}
        <section className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Daily Challenge Tile */}
            <Card 
              className={`relative overflow-hidden border ${
                isDailyCompleted 
                  ? 'border-[hsl(var(--border))] bg-[hsl(var(--secondary))]' 
                  : 'border-[hsl(var(--border))] bg-[hsl(var(--brand-blue-800))]'
              }`}
              data-testid="daily-challenge-tile"
            >
              <CardContent className="p-5">
                {isDailyCompleted ? (
                  /* ===== COMPLETED STATE - Centered Design ===== */
                  <div className="text-center">
                    {/* Green Checkmark Circle */}
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-emerald-500 flex items-center justify-center">
                      <CheckCircle className="w-10 h-10 text-emerald-500" />
                    </div>
                    
                    <h3 className="text-xl font-bold text-emerald-400 mb-1">Challenge Complete!</h3>
                    <p className="text-gray-400 text-sm mb-4">You've already completed today's challenge</p>
                    
                    {/* Stats Card */}
                    <div className="bg-[hsl(var(--brand-blue-800))] rounded-xl p-4 mb-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-[hsl(var(--brand-gold-500))]">
                            {userSubmission?.score?.total_points || userSubmission?.score?.correct_count * 100 || 0}
                          </p>
                          <p className="text-gray-400 text-sm">Total Score</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-white">
                            {userSubmission?.score?.correct_count || 0}/10
                          </p>
                          <p className="text-gray-400 text-sm">Correct</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-white">
                            {formatTime(userSubmission?.total_time_ms).replace('m ', ':').replace('s', '')}
                          </p>
                          <p className="text-gray-400 text-sm">Time</p>
                        </div>
                      </div>
                    </div>
                    
                    <Link to="/daily-challenge">
                      <Button 
                        className="bg-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white text-[hsl(var(--brand-blue-900))] font-semibold px-6"
                        data-testid="view-leaderboard-btn"
                      >
                        <Trophy className="w-4 h-4 mr-2" />
                        View Leaderboard
                      </Button>
                    </Link>
                  </div>
                ) : isDailyInProgress ? (
                  /* ===== IN PROGRESS STATE ===== */
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                        <Play className="w-6 h-6 text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">Challenge In Progress</h3>
                        <p className="text-gray-400 text-sm">
                          Question {(userSubmission?.current_clue_index || 0) + 1} of 10
                        </p>
                      </div>
                    </div>
                    
                    <Link to={`/daily-challenge/play/${userSubmission?.id}`}>
                      <Button 
                        className="w-full bg-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white text-[hsl(var(--brand-blue-900))] font-semibold"
                        data-testid="resume-challenge-btn"
                      >
                        Resume Challenge
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                ) : (
                  /* ===== NOT STARTED STATE ===== */
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-lg bg-[hsl(var(--brand-gold-500))] flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-[hsl(var(--brand-blue-900))]" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">Daily Challenge</h3>
                        <p className="text-gray-400 text-sm">10 clues • 5 categories</p>
                      </div>
                    </div>
                    
                    {/* Show ALL categories */}
                    {dailyChallenge?.categories?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {dailyChallenge.categories.map((cat, idx) => (
                          <Badge 
                            key={cat}
                            className="bg-[hsl(var(--secondary))] text-gray-300 border-[hsl(var(--border))] uppercase text-xs px-2 py-0.5"
                          >
                            {cat}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    <Link to="/daily-challenge">
                      <Button 
                        className="w-full bg-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white text-[hsl(var(--brand-blue-900))] font-semibold"
                        data-testid="start-challenge-btn"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Start Challenge
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Daily Top 3 Leaderboard Tile */}
            <Card 
              className="border-[hsl(var(--border))] bg-[hsl(var(--secondary))]"
              data-testid="daily-leaderboard-tile"
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center">
                      <Trophy className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Today's Leaders</h3>
                      <p className="text-gray-400 text-sm">{dailyChallenge?.participant_count || 0} players today</p>
                    </div>
                  </div>
                </div>
                
                {dailyLeaderboard.length > 0 ? (
                  <div className="space-y-2">
                    {dailyLeaderboard.map((entry, idx) => {
                      const medals = [
                        { icon: Crown, color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
                        { icon: Medal, color: 'text-gray-300', bg: 'bg-gray-500/20' },
                        { icon: Medal, color: 'text-amber-600', bg: 'bg-amber-600/20' }
                      ];
                      const medal = medals[idx];
                      const MedalIcon = medal.icon;
                      
                      return (
                        <div 
                          key={entry.user_id || idx}
                          className={`flex items-center gap-3 p-2.5 rounded-lg ${medal.bg}`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${medal.bg}`}>
                            <MedalIcon className={`w-5 h-5 ${medal.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{entry.display_name || 'Anonymous'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-bold">{entry.score?.correct_count || 0}/10</p>
                            <p className="text-gray-400 text-xs">{formatTime(entry.total_time_ms)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Trophy className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No completions yet today</p>
                    <p className="text-gray-500 text-xs">Be the first to claim #1!</p>
                  </div>
                )}
                
                <Link to="/daily-challenge" className="block mt-3">
                  <Button 
                    variant="outline"
                    size="sm"
                    className="w-full border-[hsl(var(--border))] text-gray-300 hover:text-white hover:border-[hsl(var(--brand-gold-500))]/50"
                  >
                    View Full Leaderboard
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ============ TRAINING MODES ============ */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">Training Modes</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Sprint Mode */}
            <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))] hover:border-[hsl(var(--brand-gold-500))]/50 transition-all h-full" data-testid="action-sprint">
              <CardContent className="p-5">
                <Link to="/sprint">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[hsl(var(--brand-gold-500))] rounded-lg flex items-center justify-center">
                      <Zap className="w-6 h-6 text-[hsl(var(--brand-blue-900))]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-white">Sprint Mode</h3>
                      <p className="text-gray-400 text-sm">
                        {stats?.sprint_sessions_played || 0} sessions • {stats?.sprint_accuracy || 0}% accuracy
                      </p>
                    </div>
                  </div>
                </Link>
                {/* View Stats Link */}
                <Link to="/sprint/stats">
                  <button className="w-full mt-3 py-2 px-3 text-sm text-[hsl(var(--brand-gold-400))] hover:text-[hsl(var(--brand-gold-300))] border border-[hsl(var(--border))] hover:border-[hsl(var(--brand-gold-500))]/50 rounded-md transition-colors flex items-center justify-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    View Detailed Stats
                  </button>
                </Link>
              </CardContent>
            </Card>

            {/* Daily Categories */}
            <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))] hover:border-violet-500/50 transition-all h-full" data-testid="action-daily-categories">
              <CardContent className="p-5">
                <Link to="/daily-categories">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <Folder className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-white">Daily Categories</h3>
                      <p className="text-gray-400 text-sm">3 featured categories daily</p>
                    </div>
                  </div>
                </Link>
                {/* View Stats Link */}
                <Link to="/daily-categories/stats">
                  <button className="w-full mt-3 py-2 px-3 text-sm text-violet-400 hover:text-violet-300 border border-[hsl(var(--border))] hover:border-violet-500/50 rounded-md transition-colors flex items-center justify-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    View Statistics
                  </button>
                </Link>
              </CardContent>
            </Card>

            {/* Play a Board */}
            <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))] hover:border-rose-500/50 transition-all h-full" data-testid="action-create-board">
              <CardContent className="p-5">
                <Link to="/boards/new">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-rose-600 to-red-700 rounded-lg flex items-center justify-center">
                      <LayoutGrid className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-white">Play a Board</h3>
                      <p className="text-gray-400 text-sm">AI-generate a custom game board and post to Community to beat your score</p>
                    </div>
                  </div>
                </Link>
                <Link to="/boards/stats">
                  <button className="w-full mt-3 py-2 px-3 text-sm text-rose-400 hover:text-rose-300 border border-[hsl(var(--border))] hover:border-rose-500/50 rounded-md transition-colors flex items-center justify-center gap-2" data-testid="boards-view-stats">
                    <BarChart3 className="w-4 h-4" />
                    View Statistics
                  </button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ============ TOOLS & PRACTICE ============ */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">Tools & Practice</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* AskAlex */}
            <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))] hover:border-[hsl(var(--brand-gold-500))]/50 transition-all h-full" data-testid="action-askalex">
              <CardContent className="p-5">
                <Link to="/askalex">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-white">AskAlex</h3>
                      <p className="text-gray-400 text-sm">Data-driven trivia insights</p>
                    </div>
                  </div>
                </Link>
                {/* View Stats Link */}
                <Link to="/askalex/portfolio">
                  <button className="w-full mt-3 py-2 px-3 text-sm text-amber-400 hover:text-amber-300 border border-[hsl(var(--border))] hover:border-amber-500/50 rounded-md transition-colors flex items-center justify-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    View Statistics
                  </button>
                </Link>
              </CardContent>
            </Card>

            {/* Beat the Score */}
            <Link to="/community">
              <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))] hover:border-emerald-500/50 transition-all cursor-pointer h-full" data-testid="action-beat-the-score">
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-600 rounded-lg flex items-center justify-center">
                      <Swords className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-white">Beat the Score</h3>
                      <p className="text-gray-400 text-sm">Play boards shared by the community and try to beat the top score</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>

            {/* Study Path */}
            <Link to="/study-path">
              <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))] hover:border-purple-500/50 transition-all cursor-pointer h-full" data-testid="action-study-path">
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <GraduationCap className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-white">Study Path</h3>
                      <p className="text-gray-400 text-sm">
                        {studyPath?.has_recommendations 
                          ? `${studyPath.total_weak_categories || 0} areas to improve` 
                          : 'Personalized practice'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>

          </div>
        </section>

        {/* ============ PERFORMANCE INSIGHTS ============ */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
            Performance Insights
          </h2>
          
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              {stats?.sprint_total_questions > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {/* Strengths */}
                  <div className="space-y-3">
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-emerald-400" />
                      Strengths
                    </h4>
                    <div className="space-y-2">
                      {topCategories.length > 0 ? (
                        topCategories.map((cat, idx) => (
                          <div key={cat.name} className="flex items-center justify-between">
                            <span className="text-gray-300 text-sm truncate max-w-[120px]">{cat.name}</span>
                            <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                              {cat.accuracy}%
                            </Badge>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">Complete more sprints to see strengths</p>
                      )}
                    </div>
                  </div>

                  {/* Focus Areas */}
                  <div className="space-y-3">
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-400" />
                      Focus Areas
                    </h4>
                    <div className="space-y-2">
                      {weakCategories.length > 0 ? (
                        weakCategories.map((cat, idx) => (
                          <div key={cat.name} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-300 text-sm truncate max-w-[100px]">{cat.name}</span>
                              <span className="text-amber-400 text-sm font-mono">{cat.accuracy}%</span>
                            </div>
                            <Progress value={cat.accuracy} className="h-1.5" />
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No weak areas detected yet!</p>
                      )}
                    </div>
                  </div>

                  {/* Recommendations */}
                  <div className="space-y-3">
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      Recommendations
                    </h4>
                    <div className="space-y-2">
                      {weakCategories.length > 0 ? (
                        <>
                          <Link to="/study-path" className="block p-2 rounded bg-purple-500/10 hover:bg-purple-500/20 transition-colors">
                            <p className="text-purple-300 text-sm">
                              Start <span className="font-semibold">{weakCategories[0]?.name}</span> study path
                            </p>
                          </Link>
                          {weakCategories.length > 1 && (
                            <p className="text-gray-400 text-xs">
                              +{weakCategories.length - 1} more areas need attention
                            </p>
                          )}
                        </>
                      ) : studyPath?.has_recommendations ? (
                        <Link to="/study-path" className="block p-2 rounded bg-purple-500/10 hover:bg-purple-500/20 transition-colors">
                          <p className="text-purple-300 text-sm">
                            Review your personalized study plan
                          </p>
                        </Link>
                      ) : (
                        <p className="text-gray-500 text-sm">Keep practicing to unlock recommendations!</p>
                      )}
                    </div>
                  </div>

                  {/* Overall Stats */}
                  <div className="space-y-3">
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-cyan-400" />
                      Overall Stats
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Questions</span>
                        <span className="text-white font-mono">{stats?.sprint_total_questions || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Accuracy</span>
                        <span className="text-cyan-400 font-mono">{stats?.sprint_accuracy || 0}%</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Daily Streak</span>
                        <span className="text-orange-400 font-mono">{stats?.current_streak || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <BarChart3 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-white font-medium mb-2">No Performance Data Yet</h3>
                  <p className="text-gray-400 mb-4">Complete Sprint sessions to see your performance insights</p>
                  <Link to="/sprint">
                    <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white">
                      <Zap className="w-4 h-4 mr-2" />
                      Start Your First Sprint
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* ============ ACHIEVEMENTS SECTION ============ */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
              Achievements
            </h2>
            <Link to="/achievements">
              <Button variant="ghost" size="sm" className="text-[hsl(var(--brand-gold-400))]">
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              {achievements?.earned?.length > 0 ? (
                <div>
                  {/* Earned Achievements Summary */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[hsl(var(--brand-gold-500))]/10 border border-[hsl(var(--brand-gold-500))]/30">
                      <Trophy className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                      <span className="text-[hsl(var(--brand-gold-400))] font-semibold">
                        {achievements.earned_count} / {achievements.total_count}
                      </span>
                      <span className="text-gray-400 text-sm">Earned</span>
                    </div>
                  </div>
                  
                  {/* Earned Badges Display */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {achievements.earned.slice(0, 6).map((item) => (
                      <div 
                        key={item.achievement?.id || item.id}
                        className="flex flex-col items-center p-3 rounded-lg bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/80 transition-colors"
                      >
                        <span className="text-3xl mb-2">{item.achievement?.icon || item.icon}</span>
                        <span className="text-white text-sm font-medium text-center truncate w-full">
                          {item.achievement?.name || item.name}
                        </span>
                        <Badge 
                          variant="outline" 
                          className={`mt-1 text-xs ${
                            (item.achievement?.tier || item.tier) === 'gold' ? 'border-yellow-500/50 text-yellow-400' :
                            (item.achievement?.tier || item.tier) === 'silver' ? 'border-gray-400/50 text-gray-300' :
                            'border-orange-500/50 text-orange-400'
                          }`}
                        >
                          {item.achievement?.tier || item.tier}
                        </Badge>
                      </div>
                    ))}
                  </div>
                  
                  {achievements.earned.length > 6 && (
                    <div className="mt-4 text-center">
                      <Link to="/achievements">
                        <Button variant="ghost" size="sm" className="text-[hsl(var(--brand-gold-400))]">
                          +{achievements.earned.length - 6} more achievements
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {/* No Achievements Yet - Show Progress */}
                  <div className="text-center mb-6">
                    <Award className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <h3 className="text-white font-medium mb-1">Start Earning Achievements</h3>
                    <p className="text-gray-400 text-sm">Complete challenges to unlock badges</p>
                  </div>
                  
                  {/* Show closest achievements to unlock */}
                  {achievements?.progress?.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-gray-400 text-sm mb-3">Next achievements to unlock:</p>
                      {achievements.progress
                        .filter(p => !p.earned && p.progress.percentage > 0)
                        .sort((a, b) => b.progress.percentage - a.progress.percentage)
                        .slice(0, 3)
                        .map((item) => (
                          <div 
                            key={item.achievement.id}
                            className="flex items-center gap-4 p-3 rounded-lg bg-[hsl(var(--accent))]"
                          >
                            <span className="text-2xl">{item.achievement.icon}</span>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-white text-sm font-medium">{item.achievement.name}</span>
                                <span className="text-gray-400 text-xs">
                                  {item.progress.current}/{item.progress.threshold}
                                </span>
                              </div>
                              <Progress value={item.progress.percentage} className="h-2" />
                            </div>
                          </div>
                        ))}
                      
                      {/* If no progress yet, show first few achievements */}
                      {achievements.progress.filter(p => !p.earned && p.progress.percentage > 0).length === 0 && (
                        <div className="grid grid-cols-3 gap-3">
                          {achievements.progress.slice(0, 3).map((item) => (
                            <div 
                              key={item.achievement.id}
                              className="flex flex-col items-center p-3 rounded-lg bg-[hsl(var(--accent))] opacity-50"
                            >
                              <span className="text-2xl mb-1 grayscale">{item.achievement.icon}</span>
                              <span className="text-gray-400 text-xs text-center truncate w-full">
                                {item.achievement.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* ============ RECENT BOARDS (Compact) ============ */}
        {recentBoards.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <LayoutGrid className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                Recent Boards
              </h2>
              <Link to="/boards">
                <Button variant="ghost" size="sm" className="text-[hsl(var(--brand-gold-400))]">
                  View All
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            
            <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
              {recentBoards.slice(0, 4).map((board) => (
                <Link key={board.id} to={`/boards/${board.id}/edit`} className="flex-shrink-0">
                  <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))] hover:border-[hsl(var(--brand-gold-500))]/50 transition-all w-[200px]">
                    <CardContent className="p-4">
                      <h4 className="text-white font-medium truncate">{board.title}</h4>
                      <p className="text-gray-400 text-sm">{board.difficulty} • {board.categories?.length || 0} cat.</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
              
              {/* Create New Board Card */}
              <Link to="/boards/new" className="flex-shrink-0">
                <Card className="bg-[hsl(var(--accent))] border-dashed border-2 border-[hsl(var(--border))] hover:border-[hsl(var(--brand-gold-500))]/50 transition-all w-[200px] h-full">
                  <CardContent className="p-4 flex flex-col items-center justify-center h-full min-h-[76px]">
                    <Plus className="w-6 h-6 text-gray-400 mb-1" />
                    <span className="text-gray-400 text-sm">Create New</span>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="pt-[130px] pb-8" data-testid="dashboard-footer">
          <div className="border-t border-[hsl(var(--border))] pt-8 flex flex-col items-center gap-3">
            <img src={LOGO_URL} alt="JepPrep" className="h-12" />
            <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-gray-500 text-sm">
              <Link to="/pricing" className="hover:text-white transition-colors" data-testid="dashboard-footer-pricing">Pricing</Link>
              <span className="hidden sm:inline">|</span>
              <Link to="/terms" className="hover:text-white transition-colors" data-testid="dashboard-footer-terms">Terms of Service</Link>
              <span className="hidden sm:inline">|</span>
              <Link to="/privacy" className="hover:text-white transition-colors" data-testid="dashboard-footer-privacy">Privacy Policy</Link>
              <span className="hidden sm:inline">|</span>
              <Link to="/blog" className="hover:text-white transition-colors" data-testid="dashboard-footer-blog">Blog</Link>
              <span className="hidden sm:inline">|</span>
              <Link to="/contact" className="hover:text-white transition-colors" data-testid="dashboard-footer-contact">Contact</Link>
            </div>
            <p className="text-gray-500 text-sm text-center max-w-2xl">
              © 2026 JepPrep. This application is an independent, fan-made trivia training tool and is not affiliated with, 
              endorsed by, or sponsored by Jeopardy!, Sony Pictures Television, or any of their subsidiaries or affiliates. 
              "Jeopardy!" is a registered trademark of Sony Pictures Television. All trademarks belong to their respective owners.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}
